import React from "react";
// ANT DESIGN COMPONENTS
import { Tag } from "antd";
// DEVICE DETECT
import { isMobile } from "react-device-detect";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faCircleCheck, faEthernet } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowDivider from "./DrawerRowDivider";
// INTERFACES
import { RootState } from "../../../app/store";
// FLATBUFFER DISPLAY HELPERS
import { formatUuidT } from "../../../flatbuffers/display";
// OPC UA endpoint reader, shared with the wizard and the Channels table so a
// record is interpreted the same way wherever it is displayed.
import { resolveChannelOpcUaEndpointUrl } from "../../setupWizard/steps/StepChannels";

function DrawerChannelInformation({ data }: any) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // A channel record reaches this drawer from three places with three different
  // shapes — the FlatBuffer decode (`opcUaEndpointUrl`, possibly as UTF-8
  // bytes), a legacy REST record (`opc_ua_endpoint_url`), and BanksDrawer's
  // `bank.channel`, which may be an empty array. The resolver returns "" for
  // every flavour that means "no endpoint", so the row below simply hides.
  const opcUaEndpointUrl = resolveChannelOpcUaEndpointUrl(data || {});

  // ************************************************* */
  // USE EFFECT ************************************** */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {/** ************************************************* */}
      {/** COMPONENT DRAWER DIVIDER */}
      <DrawerRowDivider icon={faEthernet} title={t("edgeManagement.channels.channelInformation")} />
      {/** ************************************************* */}
      <div className={`drawer__box__${theme} mt-3`}>
        {/* ID — channel.id may be a FlatBuffer UuidT; format defensively. */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>ID</p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{data && data.id ? formatUuidT(data.id) : "--"}</p>
          </div>
        </div>
        {/* STATUS */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("general.status")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">
              {data && data.active ? (
                <Tag
                  color="success"
                  icon={<FontAwesomeIcon icon={faCircleCheck} className="generalStyles__mrFix" />}
                  className="drawer__tag"
                  style={{ width: "108px", textAlign: "center" }}
                >
                  {t("userManagement.active").toUpperCase()}
                </Tag>
              ) : (
                <Tag
                  color="error"
                  icon={<FontAwesomeIcon icon={faBan} className="generalStyles__mrFix" />}
                  className="drawer__tag"
                  style={{ width: "108px", textAlign: "center" }}
                >
                  {t("userManagement.lock").toUpperCase()}
                </Tag>
              )}
            </p>
          </div>
        </div>
        {/* TRANSPORT */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>Transport</p>
          </div>
          <div className="drawer__textContainer">
            <div>
              {data && data.connection_type && (
                <>
                  {data.connection_type === "TCP" && (
                    <Tag color="blue" className="drawer__typeTag">
                      TCP
                    </Tag>
                  )}
                  {data.connection_type === "UDP" && (
                    <Tag color="cyan" className="drawer__typeTag">
                      UDP
                    </Tag>
                  )}
                  {data.connection_type === "SERIAL" && (
                    <Tag color="green" className="drawer__typeTag">
                      SERIAL
                    </Tag>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {/* NAME */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("general.name")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{data && data.name ? data.name : "--"}</p>
          </div>
        </div>
        {/* INDEX */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("edgeManagement.channels.index")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{data && data.index ? data.index : "--"}</p>
          </div>
        </div>
        {/* OPC UA ENDPOINT URL — deliberately not gated on transport. For an
            OPC UA channel this is the ONLY thing LiftCloud reads to address the
            server (`resolveOpcUaSocket` never looks at the socket columns), so
            being able to see at a glance which channel carries one — without
            opening the editor — is the point. */}
        {opcUaEndpointUrl !== "" && (
          <div className="generalStyles__flex">
            <div>
              <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                {t("edgeManagement.channels.opcUaEndpointUrl")}
              </p>
            </div>
            <div className="drawer__textContainer">
              <p className="drawer__text">{opcUaEndpointUrl}</p>
            </div>
          </div>
        )}
        {/* SERIAL: DEVICE PATH */}
        {data && data.connection_type === "SERIAL" && (
          <>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>Device Path</p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.routable_socket_address ? data.routable_socket_address : "--"}
                </p>
              </div>
            </div>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                  {t("edgeManagement.servers.baudRate")}
                </p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">{data.baud_rate ? data.baud_rate : "--"}</p>
              </div>
            </div>
          </>
        )}
        {/* TCP: HOST + VENDOR HOST */}
        {data && data.connection_type === "TCP" && (
          <>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>Host Address</p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.routable_socket_address ? data.routable_socket_address : "--"}
                </p>
              </div>
            </div>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>Vendor Address</p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.vendor_socket_address ? data.vendor_socket_address : "--"}
                </p>
              </div>
            </div>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                  {t("edgeManagement.channels.reportingSocketAddress")}
                </p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.reporting_socket_address ? data.reporting_socket_address : "--"}
                </p>
              </div>
            </div>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                  {t("edgeManagement.channels.heartbeatFrequency")}
                </p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.heartbeat_frequency ? data.heartbeat_frequency : "--"}
                </p>
              </div>
            </div>
          </>
        )}
        {/* UDP: CONTROLLER (SOURCE) + EDGE (DESTINATION) */}
        {data &&
          data.connection_type === "UDP" &&
          (() => {
            const parts = (data.routable_socket_address || "").split(":");
            const src =
              parts.length >= 2 ? `${parts[0]}:${parts[1]}` : data.routable_socket_address || "--";
            const dst = parts.length >= 4 ? `${parts[2]}:${parts[3]}` : "--";
            return (
              <>
                <div className="generalStyles__flex">
                  <div>
                    <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                      Controller IP:Port
                    </p>
                  </div>
                  <div className="drawer__textContainer">
                    <p className="drawer__text">{src}</p>
                  </div>
                </div>
                <div className="generalStyles__flex">
                  <div>
                    <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                      Edge IP:Port
                    </p>
                  </div>
                  <div className="drawer__textContainer">
                    <p className="drawer__text">{dst}</p>
                  </div>
                </div>
                <div className="generalStyles__flex">
                  <div>
                    <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                      {t("edgeManagement.channels.reportingSocketAddress")}
                    </p>
                  </div>
                  <div className="drawer__textContainer">
                    <p className="drawer__text">
                      {data.reporting_socket_address ? data.reporting_socket_address : "--"}
                    </p>
                  </div>
                </div>
                <div className="generalStyles__flex">
                  <div>
                    <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                      {t("edgeManagement.channels.heartbeatFrequency")}
                    </p>
                  </div>
                  <div className="drawer__textContainer">
                    <p className="drawer__text">
                      {data.heartbeat_frequency ? data.heartbeat_frequency : "--"}
                    </p>
                  </div>
                </div>
              </>
            );
          })()}
        {/* FALLBACK: show raw fields when connection_type is unknown */}
        {data && !["TCP", "UDP", "SERIAL"].includes(data.connection_type) && (
          <>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                  {t("edgeManagement.channels.socketAddress")}
                </p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.routable_socket_address ? data.routable_socket_address : "--"}
                </p>
              </div>
            </div>
            <div className="generalStyles__flex">
              <div>
                <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
                  {t("edgeManagement.channels.vendorSocketAddress")}
                </p>
              </div>
              <div className="drawer__textContainer">
                <p className="drawer__text">
                  {data.vendor_socket_address ? data.vendor_socket_address : "--"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DrawerChannelInformation;
